// `/profile` es de las respuestas que el contrato v1 declara opacas (`JsonOk`), así que este
// schema está escrito contra la forma observada en el backend real. Los tres arrays anidados
// (clientAssignments, standbyAssignments, recentTickets) llegan vacíos en el entorno sondeado:
// se asumen camelCase por coherencia con el resto de v1, pendiente de confirmar en el smoke.

import { z } from 'zod';
import apiClient from '@shared/api/http';
import { getFileServerUrl } from '@shared/config/env';
import type { ClientId, UserId } from '../domain/ids';
import type {
  ActivityHours,
  HoursSummary,
  Profile,
  ProfileUser,
  RecentTicket,
} from '../domain/profile';
import type { ClientAssignment, WbsAssignment } from '../domain/user';
import type { StandbyAssignment } from '../domain/external';

const FILE_SERVER_URL = getFileServerUrl();

const IdUnion = z.union([z.number(), z.string()]);

const WbsAssignmentDtoSchema = z.object({
  wbsCode: z.string(),
  sapCode: z.string().nullish(),
});

const ClientAssignmentDtoSchema = z.object({
  clientId: IdUnion,
  clientName: z.string(),
  assignedPercentage: z.number(),
  wbsAssignments: z.array(WbsAssignmentDtoSchema).optional(),
});

const StandbyAssignmentDtoSchema = z.object({
  id: IdUnion,
  userId: IdUnion,
  clientId: IdUnion.optional(),
  clientName: z.string().optional(),
  platformId: IdUnion.optional(),
  platformName: z.string().optional(),
  escalationLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  startTime: z.string(),
  endTime: z.string(),
  isActive: z.boolean(),
  assignmentNotes: z.string().optional(),
});

const HoursSummaryDtoSchema = z.object({ today: z.number(), month: z.number(), year: z.number() });

const RecentTicketDtoSchema = z.object({
  id: IdUnion,
  ticketNumber: z.string().optional(),
  title: z.string(),
  ticketType: z.string(),
  severity: z.string(),
  status: z.string(),
  clientName: z.string().optional(),
  participantRole: z.enum(['creator', 'collaborator']),
  createdAt: z.string(),
});

// v1 envía `null` explícito donde el legado omitía el campo.
const ProfileUserDtoSchema = z.object({
  id: IdUnion,
  username: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  homeCountry: z.string().nullish(),
  preferredLanguage: z.string().nullish(),
  status: z.string(),
  photoUrl: z.string().nullish(),
  role: z.object({ name: z.string() }),
});

const ProfileDtoSchema = z.object({
  user: ProfileUserDtoSchema,
  clientAssignments: z.array(ClientAssignmentDtoSchema),
  standbyAssignments: z.array(StandbyAssignmentDtoSchema),
  standbyHours: HoursSummaryDtoSchema,
  activityHours: HoursSummaryDtoSchema.extend({ totalTicketsWorked: z.number() }),
  recentTickets: z.array(RecentTicketDtoSchema),
});
type ProfileDto = z.infer<typeof ProfileDtoSchema>;

function toWbsDomain(dto: z.infer<typeof WbsAssignmentDtoSchema>): WbsAssignment {
  return { wbsCode: dto.wbsCode, sapCode: dto.sapCode ?? undefined };
}

function toClientAssignmentDomain(
  dto: z.infer<typeof ClientAssignmentDtoSchema>
): ClientAssignment {
  return {
    clientId: dto.clientId as ClientId,
    clientName: dto.clientName,
    assignedPercentage: dto.assignedPercentage,
    wbsAssignments: dto.wbsAssignments?.map(toWbsDomain),
  };
}

function toStandbyDomain(dto: z.infer<typeof StandbyAssignmentDtoSchema>): StandbyAssignment {
  return {
    id: dto.id,
    userId: dto.userId as UserId,
    clientId: dto.clientId as ClientId | undefined,
    clientName: dto.clientName,
    platformId: dto.platformId as StandbyAssignment['platformId'],
    platformName: dto.platformName,
    escalationLevel: dto.escalationLevel,
    startTime: dto.startTime,
    endTime: dto.endTime,
    isActive: dto.isActive,
    assignmentNotes: dto.assignmentNotes,
  };
}

function toHoursSummary(dto: z.infer<typeof HoursSummaryDtoSchema>): HoursSummary {
  return { today: dto.today, month: dto.month, year: dto.year };
}

function toRecentTicket(dto: z.infer<typeof RecentTicketDtoSchema>): RecentTicket {
  return {
    id: dto.id,
    ticketNumber: dto.ticketNumber,
    title: dto.title,
    ticketType: dto.ticketType,
    severity: dto.severity,
    status: dto.status,
    clientName: dto.clientName,
    participantRole: dto.participantRole,
    createdAt: dto.createdAt,
  };
}

function toProfileUser(dto: z.infer<typeof ProfileUserDtoSchema>): ProfileUser {
  return {
    id: dto.id as UserId,
    username: dto.username,
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email ?? undefined,
    phone: dto.phone ?? undefined,
    homeCountry: dto.homeCountry ?? undefined,
    preferredLanguage: dto.preferredLanguage ?? undefined,
    status: dto.status,
    photoUrl: dto.photoUrl ?? undefined,
    role: { name: dto.role.name },
  };
}

function toDomain(dto: ProfileDto): Profile {
  const activity: ActivityHours = {
    today: dto.activityHours.today,
    month: dto.activityHours.month,
    year: dto.activityHours.year,
    totalTicketsWorked: dto.activityHours.totalTicketsWorked,
  };
  return {
    user: toProfileUser(dto.user),
    clientAssignments: dto.clientAssignments.map(toClientAssignmentDomain),
    standbyAssignments: dto.standbyAssignments.map(toStandbyDomain),
    standbyHours: toHoursSummary(dto.standbyHours),
    activityHours: activity,
    recentTickets: dto.recentTickets.map(toRecentTicket),
  };
}

const UploadResponseSchema = z.object({ fileInfo: z.object({ url: z.string() }) });
const AvatarPatchResponseSchema = z.object({ photoUrl: z.string() });

export const profileRepository = {
  async getMyProfile(): Promise<Profile> {
    const { data } = await apiClient.get('/profile');
    return toDomain(ProfileDtoSchema.parse(data));
  },

  // I/O no estándar (spec.md §4): fetch directo al file server (FormData) + normalización de URL
  // + PATCH /profile/avatar. Única llamada fuera de @shared/api/http — aislada aquí para que
  // application/ no la conozca. Lanza el Error de subida tal cual si falla (mensaje preservado).
  async uploadAvatar(file: File, userId: UserId): Promise<string> {
    const formData = new FormData();
    formData.append('avatar', file);

    const uploadRes = await fetch(`${FILE_SERVER_URL}/avatar/upload?userId=${userId}`, {
      method: 'POST',
      body: formData,
    });

    if (!uploadRes.ok) {
      // Cuerpo de error de forma variable (o ausente) — se valida como unknown y se usa
      // .message solo si existe, sin asumir un contrato estricto (constitution.md regla #5).
      const raw: unknown = await uploadRes.json().catch(() => ({}));
      const message =
        raw && typeof raw === 'object' && 'message' in raw
          ? String((raw as { message?: unknown }).message)
          : undefined;
      throw new Error(message || 'Failed to upload avatar');
    }

    const { fileInfo } = UploadResponseSchema.parse(await uploadRes.json());
    const fileOrigin = FILE_SERVER_URL || window.location.origin;
    const normalizedUrl = fileInfo.url.replace(/^https?:\/\/[^/]+/, fileOrigin);

    const { data } = await apiClient.patch('/profile/avatar', { photoUrl: normalizedUrl });
    return AvatarPatchResponseSchema.parse(data).photoUrl;
  },

  // Conservado por consumo público (§6/R-5): el caso de uso de idioma del shell lo usa.
  async updateLanguage(languageCode: string): Promise<void> {
    await apiClient.patch('/profile/language', { languageCode });
  },
};
